import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import Image from "../../../components/image";
import { useTheme } from "../../../context/ThemeContext";
import ArrowRightBigIcon from "../../../images/icons/arrow-right-big-icon.inline.svg";
import ArrowRightIcon from "../../../images/icons/arrow-right-icon.inline.svg";
import { addTrailingSlash } from "../../../utils/commonFunctions";
import { Link } from "../../../utils/customLink";
import BlogCard from "./blogCard";

import "swiper/css/bundle";
import "swiper/css/navigation";

const FeaturedBlog = () => {
  const { editorialPosts, backgroundImgDark, backgroundImgLight } =
    useStaticQuery(graphql`
      query {
        editorialPosts: allWpPost(
          filter: { blogPosts: { featuredBlog: { eq: true } } }
          sort: { date: DESC }
          limit: 10
        ) {
          nodes {
            ...featuredEditorial
          }
        }

        backgroundImgDark: file(name: { eq: "featured_section_dark_bg" }) {
          ...HighQualityFileFragment
          # childImageSharp {
          #   gatsbyImageData(formats: WEBP, quality: 90, layout: FULL_WIDTH)
          # }
        }

        backgroundImgLight: file(name: { eq: "featured-bg" }) {
          ...HighQualityFileFragment
          # childImageSharp {
          #   gatsbyImageData(formats: WEBP, quality: 90, layout: FULL_WIDTH)
          # }
        }
      }
    `);

  const posts =
    editorialPosts?.nodes?.map((item) => {
      return {
        title: item?.blogPosts?.blogFeatureTitle ?? "",
        caption: item?.blogPosts?.blogSubtitle ?? "",
        description:
          item?.blogPosts?.blogSummary ?? item?.blogPosts?.blurb ?? "",
        views: "403",
        featureImg: {
          gatsbyImage: item?.globalConfig?.backgroundImage?.gatsbyImage ?? null,
          localFile: item?.globalConfig?.backgroundImage?.localFile ?? null,
          sourceUrl: item?.globalConfig?.backgroundImage?.sourceUrl ?? null
        },
        thumbImg: {
          gatsbyImage:
            item?.blogPosts?.blogInsetImage?.gatsbyImage ??
            item?.globalConfig?.backgroundImage?.gatsbyImage ??
            null,
          localFile:
            item?.blogPosts?.blogInsetImage?.localFile ??
            item?.globalConfig?.backgroundImage?.localFile ??
            null,
          sourceUrl:
            item?.blogPosts?.blogInsetImage?.sourceUrl ??
            item?.globalConfig?.backgroundImage?.sourceUrl ??
            null
        },
        category: item?.focuses?.nodes?.pop() ?? "",
        slug: addTrailingSlash(item?.slug)
      };
    }) ?? [];

  const { currentBrand, planetDomain } = useTheme();

  const swiperRef = useRef(null);
  const [swiper, setSwiper] = useState(null);

  const [featuredBlogsHeading, setFeaturedBlogsHeading] = useState(
    "Grounded in science"
  );

  const [activeBlog, setActiveBlog] = useState(posts[0]);
  const blogsite = [planetDomain, "/blog"].join("");

  useEffect(() => {
    if (currentBrand.includes("people")) {
      setFeaturedBlogsHeading("Finding Community");
    }
  }, [currentBrand]);

  if (posts?.length < 1) return <></>;

  return (
    <section className="relative flex items-center justify-center w-screen px-4 pt-24 pb-16 md:w-auto md:px-0 planet:text-white bg-secondary-bg planet:bg-transparent">
      <Image
        imageAlt={""}
        image={
          currentBrand.includes("people")
            ? backgroundImgLight
            : backgroundImgDark
        }
        imageClass="object-fill object-center absolute left-0 top-0 bottom-0 right-0 z-0 w-full"
        sourceUrl={
          currentBrand.includes("people")
            ? backgroundImgLight?.publicURL
            : backgroundImgDark?.publicURL
        }
      />

      <div className="absolute right-0 flex items-center top-9 sm:top-20">
        <div
          className="uppercase h7 !text-black-lighter planet:!text-gray-darken pr-5"
          dangerouslySetInnerHTML={{ __html: featuredBlogsHeading }}
        />

        <div className="h-2 w-14 md:w-32 rounded-l-full bg-yellow-standard planet:bg-[#4C4C4C]" />
      </div>

      <div className="container w-full md:w-4/5 md:mx-auto">
        <div className="hidden featured-blog md:block">
          {activeBlog && (
            <>
              <div className="relative flex items-center justify-center w-full md:justify-start blog-content">
                <div className="w-full md:w-[44%] md:min-h-[30rem] md:pt-5 ease-in-out blog-info z-20 text-section-title-text">
                  <div
                    className="md:w-4/5 text-[64px] leading-[95%] font-light planet:font-bold featured-shadow"
                    dangerouslySetInnerHTML={{
                      __html: activeBlog?.title || ""
                    }}
                  />
                  <div
                    className="text-[38px] leading-[46px] font-alice planet:font-body font-normal planet:font-light py-1"
                    dangerouslySetInnerHTML={{
                      __html: activeBlog?.caption || ""
                    }}
                  />

                  <div
                    className="md:w-3/5 md:pr-8 blog-content__description text-base leading-[155%]"
                    dangerouslySetInnerHTML={{
                      __html: activeBlog?.description || ""
                    }}
                  />

                  <Link
                    to={`${blogsite}/${activeBlog.slug}`}
                    className="flex items-center justify-center gap-3 px-6 py-2 mt-5 font-bold border rounded-full w-max planet:rounded-sm border-black-lighter planet:border-white text-black-lighter planet:text-white"
                  >
                    {"Read the article"}
                    <ArrowRightBigIcon className="block fill-black-lighter planet:hidden" />
                    <ArrowRightIcon className="hidden stroke-white planet:block" />
                  </Link>
                </div>
                <div className="relative z-10 w-full bg-transparent md:absolute md:right-0 md:top-2 md:w-3/4">
                  <div className="w-full h-[500px] mt-9 mb-12 card-shadow">
                    <Image
                      imageAlt={""}
                      image={activeBlog?.featureImg?.gatsbyImage}
                      imageClass="relative object-cover object-center w-full h-full rounded md:rounded-xs"
                      sourceUrl={activeBlog?.featureImg?.sourceUrl}
                    />
                    <Image
                      imageAlt={""}
                      image={activeBlog?.thumbImg?.gatsbyImage}
                      imageClass="absolute object-cover object-center -bottom-[2.5rem] -right-[2.5rem] sm:w-[15rem] sm:h-[15rem] rounded md:rounded-xs gvi-box-shadow card-shadow"
                      sourceUrl={activeBlog?.thumbImg?.sourceUrl}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="relative w-full mt-4 blog-slides md:mt-36">
          <div className="pb-5 md:pt-2">
            <Swiper
              ref={swiperRef}
              speed={1300}
              onSwiper={setSwiper}
              grabCursor={true}
              spaceBetween={0}
              loop={true}
              className="z-20"
              slidesPerView={1.25}
              onSlideChange={(swiperCore) => {
                const { realIndex } = swiperCore;
                setActiveBlog(posts[realIndex]);
              }}
              breakpoints={{
                480: {
                  slidesPerView: 1.25,
                  spaceBetween: 0
                },
                640: {
                  slidesPerView: posts?.length > 3 ? 3 : posts?.length,
                  spaceBetween: 0
                },
                1024: {
                  slidesPerView: posts?.length > 6 ? 6 : posts?.length,
                  spaceBetween: 15
                }
              }}
            >
              {posts?.map((blog, idx) => (
                <SwiperSlide key={idx}>
                  <BlogCard blog={blog} titleClass="text-base font-semibold" />
                </SwiperSlide>
              ))}
            </Swiper>

            <div
              className="swiper-button-prev !-left-16 !w-11 h-11 after:hidden !hidden md:!block"
              onClick={() => {
                swiper.slidePrev();
              }}
            />

            <div
              className="swiper-button-next rotate-180 !-right-16 !w-11 h-11 after:hidden !hidden md:!block"
              onClick={() => {
                swiper.slideNext();
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedBlog;
